/**
 * 店舗API クライアント
 */

import { authFetch, API_BASE_URL } from "./client";

/**
 * 店舗レスポンスの型
 */
export interface StoreResponse {
    id: number;
    organization_id: number | null;
    name: string;
    code: string | null;
    is_active: boolean;
    /** 稼働状況 (1=稼働前, 2=稼働中, 3=閉店, 4=その他) */
    status: number;
    /** 略称 (level=11 organization の short_name から解決) */
    short_name: string | null;
    created_at: string | null;
    updated_at: string | null;
}

/**
 * 店舗一覧レスポンスの型
 */
export interface StoreListResponse {
    items: StoreResponse[];
    total: number;
}

/**
 * 店舗作成リクエストの型
 */
export interface StoreCreateRequest {
    name: string;
    code?: string | null;
    /** 所属組織 (新規 level=11 org の親) の id */
    parent_organization_id: number;
    /** 略称 (level=11 org の short_name) */
    short_name?: string | null;
    /** 稼働状況 (1=稼働前, 2=稼働中, 3=閉店, 4=その他) */
    status?: number;
    /** 使用部門 (department.id の配列) */
    department_ids?: number[];
}

/**
 * 店舗更新リクエストの型
 */
export interface StoreUpdateRequest {
    name?: string;
    code?: string | null;
    is_active?: boolean;
    /** 稼働状況 (1=稼働前, 2=稼働中, 3=閉店, 4=その他) */
    status?: number;
    /** 略称 (organization.short_name を更新) */
    short_name?: string | null;
    /** 所属組織変更時のみ指定。level=11 org の親 id を変更する */
    parent_organization_id?: number;
    /** 使用部門のフルセット。指定時は差分計算で再構築 */
    department_ids?: number[];
}

/**
 * 店舗一覧を取得
 */
export async function fetchStores(params?: {
    is_active?: boolean;
    tenant_id?: string;
}): Promise<StoreListResponse> {
    const searchParams = new URLSearchParams();

    if (params?.is_active !== undefined) {
        searchParams.set("is_active", String(params.is_active));
    }

    if (params?.tenant_id !== undefined) {
        searchParams.set("tenant_id", params.tenant_id);
    }

    const queryString = searchParams.toString();
    const url = `${API_BASE_URL}/stores${queryString ? `?${queryString}` : ""}`;

    const response = await authFetch(url);
    if (!response.ok) {
        throw new Error(`店舗一覧の取得に失敗しました: ${response.status}`);
    }
    return response.json();
}

/**
 * 店舗詳細を取得
 */
export async function fetchStore(storeId: number): Promise<StoreResponse> {
    const response = await authFetch(`${API_BASE_URL}/stores/${storeId}`);
    if (!response.ok) {
        throw new Error(`店舗の取得に失敗しました: ${response.status}`);
    }
    return response.json();
}

/**
 * 店舗を作成
 */
export async function createStore(data: StoreCreateRequest): Promise<StoreResponse> {
    const response = await authFetch(`${API_BASE_URL}/stores`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
    });
    if (!response.ok) {
        const body = await response.text();
        throw new Error(`店舗の作成に失敗しました: ${response.status} ${body}`);
    }
    return response.json();
}

/**
 * 店舗を更新
 */
export async function updateStore(
    storeId: number,
    data: StoreUpdateRequest
): Promise<StoreResponse> {
    const response = await authFetch(`${API_BASE_URL}/stores/${storeId}`, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
    });
    if (!response.ok) {
        throw new Error(`店舗の更新に失敗しました: ${response.status}`);
    }
    return response.json();
}

/**
 * 店舗を削除（論理削除）
 */
export async function deleteStore(storeId: number): Promise<void> {
    const response = await authFetch(`${API_BASE_URL}/stores/${storeId}`, {
        method: "DELETE",
    });
    if (!response.ok) {
        throw new Error(`店舗の削除に失敗しました: ${response.status}`);
    }
}
